import React from 'react';
import {
  CapabilityDecision,
  Responsibility,
  SkillLevel,
  Resolution,
  CAPABILITY_DOMAIN_LABELS,
  RESPONSIBILITY_LABELS,
  SKILL_LEVEL_LABELS,
  RESOLUTION_LABELS,
} from './types';
import { shouldAskLevel, shouldAskResolution } from './capabilityDecision.service';

interface CapabilityDomainStepProps {
  decision: CapabilityDecision;
  onUpdate: (updates: Partial<CapabilityDecision>) => void;
}

/**
 * CapabilityDomainStep - Single domain assessment
 * 
 * Keuzedwingend, feitelijk, zonder coachtaal.
 * Implementeert flow-logica: vraag 2, 3, 4 afhankelijk van antwoorden.
 */
const CapabilityDomainStep: React.FC<CapabilityDomainStepProps> = ({
  decision,
  onUpdate,
}) => {
  const domainLabel = CAPABILITY_DOMAIN_LABELS[decision.domain];

  return (
    <div className="space-y-6">
      {/* Domain header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900">{domainLabel}</h3>
      </div>

      {/* Question 1: Is dit domein nodig? */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Is dit domein nodig voor dit doel?
        </label>
        <div className="flex space-x-3">
          <button
            onClick={() => onUpdate({ required: true })}
            className={`
              px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors
              ${decision.required === true
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-300 text-gray-700 hover:border-gray-400'
              }
            `}
          >
            Ja
          </button>
          <button
            onClick={() => onUpdate({ required: false, responsibility: undefined, level: undefined, resolution: undefined })}
            className={`
              px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors
              ${decision.required === false
                ? 'border-gray-900 bg-gray-900 text-white'
                : 'border-gray-300 text-gray-700 hover:border-gray-400'
              }
            `}
          >
            Nee
          </button>
        </div>
      </div>

      {/* Question 2: Wie draagt verantwoordelijkheid? (only if required = true) */}
      {decision.required === true && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Wie draagt hier verantwoordelijkheid voor?
          </label>
          <div className="space-y-2">
            {(['self', 'other', 'unknown'] as Responsibility[]).map(resp => (
              <button
                key={resp}
                onClick={() => onUpdate({ 
                  responsibility: resp,
                  // Reset level and resolution when changing responsibility
                  level: undefined,
                  resolution: undefined,
                })}
                className={`
                  w-full px-4 py-3 rounded-lg border-2 text-sm font-medium text-left transition-colors
                  ${decision.responsibility === resp
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                  }
                `}
              >
                {RESPONSIBILITY_LABELS[resp]}
              </button>
            ))}
          </div>
          {/* Warning for 'unknown' */}
          {decision.responsibility === 'unknown' && (
            <p className="text-sm text-gray-600 italic">
              Kies een verantwoordelijkheid om door te gaan.
            </p>
          )}
        </div>
      )}

      {/* Question 3: Wat is je niveau? (only if responsibility = self) */}
      {shouldAskLevel(decision) && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Wat is je niveau?
          </label>
          <div className="space-y-2">
            {(['basic', 'workable', 'advanced'] as SkillLevel[]).map(level => (
              <button
                key={level}
                onClick={() => onUpdate({ 
                  level,
                  // Reset resolution when changing level
                  resolution: level === 'advanced' ? undefined : decision.resolution,
                })}
                className={`
                  w-full px-4 py-3 rounded-lg border-2 text-sm font-medium text-left transition-colors
                  ${decision.level === level
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                  }
                `}
              >
                {SKILL_LEVEL_LABELS[level]}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Question 4: Hoe los je dit op? (only if level = basic | workable) */}
      {shouldAskResolution(decision) && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Hoe ga je dit oplossen?
          </label>
          <div className="space-y-2">
            {(['learn', 'outsource', 'hire'] as Resolution[]).map(res => (
              <button
                key={res}
                onClick={() => onUpdate({ resolution: res })}
                className={`
                  w-full px-4 py-3 rounded-lg border-2 text-sm font-medium text-left transition-colors
                  ${decision.resolution === res
                    ? 'border-gray-900 bg-gray-900 text-white'
                    : 'border-gray-300 text-gray-700 hover:border-gray-400'
                  }
                `}
              >
                {RESOLUTION_LABELS[res]}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CapabilityDomainStep;
